import { cn } from "@/lib/utils";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva("inline-flex items-center justify-center", {
    variants: {
        variant: {
            default: "h-9 rounded-lg bg-muted p-1 text-muted-foreground",
            modern: "rounded-xl bg-white p-1.5 shadow-[0_2px_8px_-4px_rgba(0,0,0,0.10)]",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

const tabsTriggerVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: [
                    "rounded-md px-3 py-1 ring-offset-background",
                    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
                ],
                modern: [
                    "relative rounded-lg px-6 py-2.5 transition-all duration-300 ease-out text-md",
                    "text-text-body hover:text-text-mainTitle hover:after:w-24",
                    "data-[state=active]:text-text-mainTitle",
                    "after:absolute after:bottom-0 after:left-1/2 after:h-0.5 after:w-0 after:-translate-x-1/2 after:bg-gradient-to-r after:from-primary-base after:to-primary-dark after:transition-all after:duration-300 after:ease-out",
                    "data-[state=active]:after:w-24 data-[state=active]:after:h-0.5",
                ],
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

const tabsContentVariants = cva(
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    {
        variants: {
            variant: {
                default: "mt-2 ring-offset-background focus-visible:ring-ring",
                modern: "mt-4 focus-visible:ring-primary-base/20",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

interface TabsListProps
    extends
        React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
        VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.List>,
    TabsListProps
>(({ className, variant, ...props }, ref) => (
    <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ variant, className }))}
        {...props}
    />
));
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps
    extends
        React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
        VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Trigger>,
    TabsTriggerProps
>(({ className, variant, ...props }, ref) => (
    <TabsPrimitive.Trigger
        ref={ref}
        className={cn(tabsTriggerVariants({ variant, className }))}
        {...props}
    />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

interface TabsContentProps
    extends
        React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>,
        VariantProps<typeof tabsContentVariants> {}

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    TabsContentProps
>(({ className, variant, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(tabsContentVariants({ variant, className }))}
        {...props}
    />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    tabsListVariants,
    tabsTriggerVariants,
    tabsContentVariants,
};
export type { TabsListProps, TabsTriggerProps, TabsContentProps };
